import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import {PathService} from "./path.service";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {OkInfo} from "../util/interfaces";

export interface NameServerGroup {
    name: string;
    hosts: string[]
}

export interface DomainInfo {
    name: string;
    in_registry: boolean;
    registered: number;
    updated: number;
    expires: number;
    transferred_out: number|null;
    ns_group: NameServerGroup;
    status: string[];
    flags: {[key: string]: boolean};
}

interface Matches {
    matches: string[];
}

interface NameServerGroupResponse {
    groups: NameServerGroup[];
}

interface CheckResponse {
    available: boolean;
}

export interface BulkResponse {
    remainingDomains?: string[];
    notInRegistry?: string[];
    alreadyDeleted?: string[];
    alreadyDone?: string[];
    inPrimaryDns?: string[];
    outOfGracePeriod?: string[];
    inServerHold?: string[];
    inServerDeleteProhibited?: string[];
    flagExcluded?: {[name: string]: string[]};
}

export interface FlagWithCount {
    name: string;
    preventsDelete: boolean;
    domainCount: number;
}

export interface FlagDomainInfo {
    name: string;
    assigned: boolean;
}

export interface FlagDomainsResponse {
    totalCount: number;
    domains: FlagDomainInfo[];
}

export interface MatchingDomainInfo {
    name: string;
    in_registry: boolean;
    status: string[];
    expired: boolean;
    ns_group: string;
    flags: string[];
}

export interface MatchingDomainsResponse {
    total_count: number;
    domains: MatchingDomainInfo[];
}

export interface EppBaseDomainInfo {
    name: string;
    in_registry: boolean;
    valid: boolean;
}

export interface EppFullDomainInfo extends EppBaseDomainInfo {
    ns_hosts: string[];
    statuses: string[];
    created: number;
    updated: number;
    expires: number;
    auth_code: string;
}

export type EppDomainInfo = EppBaseDomainInfo | EppFullDomainInfo;

export interface StatusSummary {
    total: number;
    ok: number;
    expired: number;
    pendingDelete: number;
    pendingTransfer: number;
    serverHold: number;
    clientProhibited: number;
    serverDeleteProhibited: number;
    serverUpdateProhibited: number;
    serverTransferProhibited: number;
}


@Injectable({
    providedIn: 'root'
})
export class DomainService {

    private readonly domainRoot: string;

    constructor(private http: HttpClient,
                private pathService: PathService) {
        this.domainRoot = `${this.pathService.apiRoot}/domain`;
    }

    getInfo(domainName: string): Observable<DomainInfo> {
        return this.http.get<DomainInfo>(`${this.domainRoot}/info/${domainName}`).pipe(
            map((info) => {
                if (!info.status)
                    info.status = [];
                return info;
            })
        );
    }

    find(domainPartial: string): Observable<string[]> {
        return this.http.get<Matches>(`${this.domainRoot}/find/${domainPartial}`).pipe(
            map((out) => out.matches || [])
        );
    }

    getMatchingDomains(partialName: string|null=null, inRegistry: boolean|null=null, expired: boolean|null=null,
                       status: string|null=null, nsGroup: string|null=null, flag: string|null=null,
                       page?: number, size?: number): Observable<MatchingDomainsResponse> {
        let params = new HttpParams();
        if (partialName !== null)
            params = params.set('partial_name', partialName);
        if (inRegistry !== null)
            params = params.set('in_registry', inRegistry);
        if (expired !== null)
            params = params.set('expired', expired);
        if (status !== null)
            params = params.set('status', status);
        if (nsGroup !== null)
            params = params.set('ns_group', nsGroup);
        if (flag !== null)
            params = params.set('flag', flag);
        if (page !== undefined)
            params = params.set('page', page);
        if (size !== undefined)
            params = params.set('size', size);
        return this.http.get<MatchingDomainsResponse>(`${this.domainRoot}/matches`, {params: params}).pipe(
            map(resp => {
                return {
                    total_count: resp.total_count,
                    domains: resp.domains? resp.domains.map(d => {
                                            if (!d.status)
                                                d.status = [];
                                            if (!d.flags)
                                                d.flags = [];
                                            return d;
                                        }
                                    )
                                : []
                };
            })
        );
    }

    markInRegistry(domainName: string, inRegistry: boolean): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/in-registry/${domainName}`, {inRegistry: inRegistry});
    }

    updateDomainFlag(domainName: string, flagName: string, flagValue: boolean): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/flag/${domainName}`, {flagName: flagName, flagValue: flagValue});
    }

    getAllFlags(): Observable<String[]> {
        return this.http.get<String[]>(`${this.domainRoot}/flags`);
    }

    getFlagsWithCounts(): Observable<FlagWithCount[]> {
        return this.http.get<FlagWithCount[]>(`${this.domainRoot}/flags/with-counts`);
    }

    createFlag(name: string, preventsDelete: boolean): Observable<OkInfo> {
        return this.http.post<OkInfo>(`${this.domainRoot}/flags`, { name, preventsDelete });
    }

    updateFlag(flagName: string, newName: string, preventsDelete: boolean): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/flags/${flagName}`, { name:newName, preventsDelete });
    }

    deleteFlag(flagName: string, forceDelete: boolean=false): Observable<OkInfo> {
        return this.http.delete<OkInfo>(`${this.domainRoot}/flags/${flagName}/${forceDelete}`);
    }

    getDomainsByFlag(flagName: string, page: number, size: number): Observable<FlagDomainsResponse> {
        return this.http.get<FlagDomainsResponse>(`${this.domainRoot}/flags/${flagName}/domains?page=${page}&size=${size}`);
    }

    bulkSetFlag(flagName: string, domains: string[]): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/set-flag/${flagName}`, {domains: domains});
    }

    bulkClearFlag(flagName: string, domains: string[]): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/clear-flag/${flagName}`, {domains: domains});
    }

    bulkLockDomains(domains: string[]): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/lock`, {domains: domains});
    }

    bulkUnlockDomains(domains: string[]): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/unlock`, {domains: domains});
    }

    bulkChangeDomainsNsGroup(domains: string[], newGroupName: string): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/change-ns-group/${newGroupName}`,
                                           {domains: domains});
    }

    bulkDeleteDomains(domains: string[]): Observable<BulkResponse> {
        return this.http.put<BulkResponse>(`${this.domainRoot}/bulk/delete`, {domains: domains});
    }

    getNameServerGroups(): Observable<NameServerGroup[]> {
        return this.http.get<NameServerGroupResponse>(`${this.domainRoot}/nsgroups`).pipe(
            map((out) => out.groups)
        );
    }

    eppGetInfo(domainName: string): Observable<EppDomainInfo> {
        return this.http.get<EppDomainInfo>(`${this.domainRoot}/epp/info/${domainName}`);
    }

    create(domainName: string, nsGroupName: string): Observable<OkInfo> {
        return this.http.post<OkInfo>(`${this.domainRoot}/epp/create`, {
            domainName: domainName,
            nsGroupName: nsGroupName
        });
    }

    delete(domainName: string): Observable<OkInfo> {
        return this.http.delete<OkInfo>(`${this.domainRoot}/${domainName}`);
    }

    lock(domainName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/epp/lock/${domainName}`, {});
    }

    unlock(domainName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/epp/unlock/${domainName}`, {});
    }

    syncDomainDates(domainName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/sync/${domainName}`, {});
    }

    syncDomainStatus(domainName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/sync_status/${domainName}`, {});
    }

    getStatusSummary(): Observable<StatusSummary> {
        return this.http.get<StatusSummary>(`${this.domainRoot}/status_summary`);
    }

    changeNameServerGroup(domainName: string, groupName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/${domainName}/nsgroup/${groupName}`, {});
    }

    approveTransfer(domainName: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.domainRoot}/${domainName}/transfer`, {});
    }

    isAvailable(domainName: string): Observable<boolean> {
        return this.http.get<CheckResponse>(`${this.domainRoot}/epp/check/${domainName}`).pipe(
            map((out) => out.available)
        );
    }
}
